/**
 * The AI assistant on the dispatch board — the dashboard's chat agent, on a
 * phone, for the office.
 *
 * THREE PARTS, ONE STATE — the same shape as DispatcherDevice beside it:
 *
 *   the launcher                the only way in: ONE floating control, bottom
 *                               right, on every dispatcher screen — and it
 *                               names what the screen behind it is about
 *   the sheet                   the conversation, the composer, and the one
 *                               control that gets you out of a hung turn
 *   the hook                    `useChatAgent`, held by the FRAME rather than
 *                               by the sheet, which is what lets a dispatcher
 *                               close the sheet, look at the board, and come
 *                               back to a finished answer
 *
 * ────────────────────────────────────────────────────────────────────────────
 * WHY IT IS IN THE FRAME AND NOT ON THE BOARD.
 *
 * A dispatcher has two screens — the board and one trip — and the question
 * they want to ask is as likely to occur on one as the other ("who else is
 * free at six", "what did we agree with this operator"). A control on the
 * board would be missing from the trip and vice versa, and putting it on both
 * is two of them to keep in step. DispatcherDevice is in the frame for the
 * same reason.
 *
 * IT IS ONE DOOR, AND IT MOVED. It used to be a 44px icon in the dark bar
 * beside the bell. It is now a floating launcher at the bottom right, which is
 * where the dashboard's own chat widget sits, so the same question is asked
 * from the same corner on a desk and on a phone. The bar's icon is GONE — not
 * kept "as well as": two doors to one sheet is a second control to keep in
 * step and one more thing on a screen that must not grow. Nothing is added to
 * the board's content and nothing to a trip's action rows.
 *
 * AND IT KNOWS WHAT IS ON SCREEN. On a trip it offers that trip by its
 * BOOKING REFERENCE, so nobody retypes one off the screen behind the sheet;
 * on the board it offers the period the board is showing. Both arrive the same
 * way — the screen publishes its own subject through `useAskSubject`
 * (askSubject.js, which holds the whole reasoning), because the screen is what
 * holds the loaded trip and the resolved range, and the frame cannot read a
 * child route's params. The mongo `:bookingId` in the URL is never read for
 * this and never shown: a reference is what a dispatcher says out loud, and an
 * ObjectId is what the agent would be unable to use.
 *
 * THE CONTEXT GOES IN THE COMPOSER, NOT ON THE WIRE. Opening the launcher
 * SEEDS the box with "About PU_DASH_…: " and leaves it visible and editable.
 * Two reasons, and they point the same way: the invocation body stays exactly
 * `{prompt}` — the token is the whole identity and a second structured field
 * beside it would be a second thing to authorise — and a dispatcher can read,
 * correct or delete whatever the agent is being told about their screen before
 * it goes. Nothing about the current screen is sent that is not in the box.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * THE CHAT IS PRIVATE TO THE PERSON SIGNED IN, and the sheet says so.
 *
 * The agent's long-term memory is addressed by `actorId`, which is the Cognito
 * `sub` of the bearer token the Runtime verified — so one dispatcher's
 * remembered counterparties, bookings and prices are never read into
 * another's, and the office has no shared chat. That is a property of the
 * TOKEN, not of this screen, which is exactly why this screen must hand over
 * the signed-in person's own token and nothing else (see `portalAgentToken`).
 *
 * ────────────────────────────────────────────────────────────────────────────
 * WHAT THE DISPATCHER-ONLY GATE IS, AND WHAT IT IS NOT.
 *
 * A driver or an operator never sees this, because `/portal/dispatcher` is
 * behind `requireKind={DISPATCHER}` (PortalRoutes → PortalRoute → KindGate)
 * and this component only exists inside that frame. That is a UX and scope
 * decision and it is NOT a security boundary: the AgentCore Runtime accepts
 * any token from the DASHBOARD pool and authorises each tool call against the
 * caller's own `cognito:groups`, so what a token may do is decided server-side
 * per tool, not by which screen offered the box. A driver's or operator's
 * token is from the PORTAL pool, which the Runtime's authorizer does not
 * accept at all — that refusal, not this file, is what keeps them out.
 */
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import useChatAgent from '../../../Components/ChatWidget/useChatAgent';
import { ensurePortalToken } from '../../../Services/PortalApi';
import BottomSheet from '../Components/BottomSheet';
import { AssistantIcon, SendIcon, StopIcon } from '../Components/Icons';
import AssistantBlock from './AssistantBlock';

/* --- copy (English: these are staff screens) ----------------------------- */
/**
 * The launcher's base name, and the stem of every contextual one.
 *
 * Kept as a PREFIX rather than swapped out, so "Ask the assistant" is always
 * the beginning of the control's accessible name whatever screen it is on.
 * The portal's own guards match on it (DispatcherAssistantGate), and so does
 * the end-to-end flow.
 */
const BUTTON_LABEL = 'Ask the assistant';
const SHEET_TITLE = 'Assistant';
const SHEET_SUBTITLE = 'Bookings, drivers, operators and prices — ask in your own words';
const PLACEHOLDER = 'Ask about a booking, a driver, a price…';
const SEND = 'Send';
const STOP = 'Stop';
const WORKING = 'Working…';
const NEW_CHAT = 'Start a new chat';
const EMPTY_LEAD = 'Ask anything you would ask the office. It can read the board, '
  + 'bookings, drivers and what we pay operators — the same things you can.';
/**
 * Said on the screen, not only in a comment. A dispatcher deciding whether to
 * type a customer's name into a box is entitled to know who else can read it,
 * and the answer is nobody.
 */
const PRIVACY_NOTE = 'This chat is yours. What it remembers is tied to your own login — '
  + 'it is not shared with the office or with another dispatcher.';
const INTERRUPTED = 'The answer stopped before it finished — the connection dropped, '
  + 'or you stopped it.';
const ASK_AGAIN = 'Ask again';

/**
 * The empty state's openers. They FILL the box rather than send, because the
 * next thing a dispatcher does is change a word ("tomorrow" → "Friday"), and
 * because an accidental tap should not invoke a model.
 */
const OPENERS = [
  'What is unassigned tomorrow?',
  'Who is working today?',
  'Which trips are running late right now?',
];

/**
 * Where the bearer token comes from on THIS surface.
 *
 * `ensurePortalToken` and nothing else. Three things follow from that, and all
 * three are deliberate:
 *
 *   - it reads the PORTAL's store (`PORTAL_SESSION_TOKEN`), which for office
 *     staff holds the dashboard id token `storeStaffSession` put there. The
 *     admin store and `Services/API`'s request interceptor are never touched;
 *     that separation is the whole reason PortalApi exists, and it strips any
 *     `Authorization` it did not set itself;
 *   - it RENEWS a nearly-expired token before the invocation goes out. A
 *     dispatcher keeps the board open all shift, and the Runtime's authorizer
 *     checks `exp` before any of our code runs;
 *   - it is the SIGNED-IN person's token, so the `sub` the agent scopes its
 *     memory by is theirs. A constant or shared token here would quietly merge
 *     two dispatchers' remembered context.
 *
 * Module-level so its identity is stable: `useChatAgent` builds the service in
 * an effect keyed on it, and a new function each render would rebuild the
 * service — and abort the stream — on every keystroke.
 */
export const portalAgentToken = () => ensurePortalToken();

/* --- what the screen behind the launcher is about ------------------------ */

/**
 * What the launcher SAYS, and what opening it puts in the box.
 *
 * On a trip the reference goes in both: the control names the trip it will ask
 * about, and the box opens with that trip already named so the first thing
 * typed is the question. On the board the control keeps its plain name — the
 * range tabs right above it already say which period is showing, and saying it
 * twice is the duplication these screens are kept clear of — but the box still
 * opens with the period in it, because the AGENT has no other way to know it.
 *
 * Pure, and exported, so the specs can read the contract without a DOM.
 */
export function askContext(subject) {
  if (subject?.kind === 'trip' && subject.value) {
    return {
      label: `${BUTTON_LABEL} about ${subject.value}`,
      seed: `About ${subject.value}: `,
    };
  }

  if (subject?.kind === 'board' && subject.value) {
    return { label: BUTTON_LABEL, seed: `About the board for ${subject.value}: ` };
  }

  return { label: BUTTON_LABEL, seed: '' };
}

/**
 * The assistant's whole state. Called ONCE, by the frame, so the launcher, the
 * dot on it and the sheet can never disagree — and so the conversation
 * outlives the sheet being closed.
 */
export function useDispatcherAssistant() {
  const chat = useChatAgent({
    // NOT the default keys: those are the dashboard's floating widget, and a
    // dispatcher who uses both would otherwise have one conversation spread
    // across a desk and a phone, each `reset()` wiping the other. Long-term
    // memory is actor-scoped rather than session-scoped, so what the agent
    // remembers about them still crosses over; the visible thread does not.
    storageNamespace: 'portal-dispatcher',
    getAuthToken: portalAgentToken,
  });
  const [open, setOpen] = useState(false);
  // What the composer opens with — the screen's own subject, put there by the
  // launcher. Held here rather than in the sheet because the sheet is mounted
  // fresh on every open and reads this as its initial value.
  const [seed, setSeed] = useState('');
  // An answer that landed while the sheet was shut. The reason to close it
  // mid-answer is to look at the board, so something has to say it is back.
  const [unseen, setUnseen] = useState(false);
  const wasPending = useRef(false);

  useEffect(() => {
    const finished = wasPending.current && !chat.pending;
    wasPending.current = chat.pending;
    if (finished && !open) setUnseen(true);
  }, [chat.pending, open]);

  return {
    chat,
    open,
    unseen,
    seed,
    show: (text = '') => { setUnseen(false); setSeed(text); setOpen(true); },
    close: () => setOpen(false),
  };
}

/**
 * THE PERMANENT WAY IN: floating, bottom right, on every dispatcher screen.
 *
 * NO `aria-label`. The name is computed from the visible text, which is the
 * whole point — WCAG's "Label in Name" asks that what a control is CALLED
 * contains what it SAYS, and an `aria-label` reading "Ask the assistant" over
 * visible text reading "Ask the assistant about PU_DASH_…" would be two
 * different controls to a screen-reader user and a sighted one. The icon is
 * `aria-hidden` (every portal icon is) and so is the dot, so the accessible
 * name is exactly the sentence on screen.
 *
 * The reference can be long and the screen is 390px wide, so the text is
 * capped and ellipsised in CSS rather than allowed to set the pill's width.
 */
export function DispatcherAssistantLauncher({ assistant, subject }) {
  const { label, seed } = askContext(subject);

  return (
    <button
      type="button"
      className="pp-ai__launch"
      aria-haspopup="dialog"
      aria-expanded={assistant.open}
      onClick={() => assistant.show(seed)}
    >
      <AssistantIcon size={20} />
      <span className="pp-ai__launch-text">{label}</span>
      {assistant.unseen ? <span className="pp-ai__dot" aria-hidden="true" /> : null}
    </button>
  );
}

/** The openers, shown only while there is nothing else in the thread. */
function Openers({ onPick }) {
  return (
    <div className="pp-ai__empty">
      <p className="pp-ai__empty-lead">{EMPTY_LEAD}</p>
      <div className="pp-ai__openers">
        {OPENERS.map((text) => (
          <button
            key={text}
            type="button"
            className="pp-btn pp-btn--sm pp-btn--outline"
            onClick={() => onPick(text)}
          >
            {text}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * What the turn is doing, and the way out of it.
 *
 * Three states, one region, `role="status"` so a screen reader hears the
 * change without the streaming text itself being announced letter by letter:
 *   working       a turn is in flight
 *   stopped       the body closed without the server finishing the turn —
 *                 the ordinary outcome of mobile data, so it offers the
 *                 question again rather than reading as a fault
 *   failed        the request or the server said why
 * Both of the bad ones are RECOVERABLE in one tap, and neither of them leaves
 * the composer disabled: `useChatAgent` clears `pending` at the end of the
 * stream however it ended.
 */
function TurnState({ chat, onAskAgain }) {
  if (chat.pending) {
    return (
      <p className="pp-ai__state" role="status">
        <span className="pp-ai__dots" aria-hidden="true" />
        {WORKING}
      </p>
    );
  }

  /**
   * INTERRUPTED IS CHECKED FIRST, AND THAT ORDER IS THE WHOLE POINT.
   *
   * A real dropped connection sets BOTH of these. A phone losing the network
   * mid-answer does not give the client a tidy end-of-body: the chunked
   * response is terminated without its terminal chunk, so the browser errors
   * the stream — the fetch rejects AND no `turn.complete` ever arrives. Tested
   * the other way round, a dispatcher at the kerb was shown `network error`
   * (or whatever that browser calls a dead socket) and nothing else, which
   * says neither what happened to their answer nor what to do about it.
   *
   * So the sentence leads and the cause goes underneath it, quietly. The cause
   * is NOT dropped: a message with the reason thrown away is indistinguishable
   * from a swallowed bug, and the next person debugging this from a screenshot
   * needs the string.
   *
   * A turn the SERVER ended with an `error` event is not interrupted at all
   * (`completedRef` is set for it), so it keeps the plain error below — it has
   * a real explanation to give and did not drop anything.
   */
  if (chat.interrupted) {
    return (
      <div className="pp-ai__recover" role="status">
        <p className="pp-ai__state">{INTERRUPTED}</p>
        {chat.error ? <p className="pp-ai__why">{chat.error}</p> : null}
        <button type="button" className="pp-btn pp-btn--sm pp-btn--outline" onClick={onAskAgain}>
          {ASK_AGAIN}
        </button>
      </div>
    );
  }

  if (chat.error) {
    return (
      <div className="pp-ai__recover" role="status">
        <p className="pp-form__error">{chat.error}</p>
        <button type="button" className="pp-btn pp-btn--sm pp-btn--outline" onClick={onAskAgain}>
          {ASK_AGAIN}
        </button>
      </div>
    );
  }

  return null;
}

/** The conversation, the composer, and the state line between them. */
export function DispatcherAssistantSheet({ assistant }) {
  const { chat } = assistant;
  const {
    blocks, pending, send, answer, decideApproval, stop, reset,
  } = chat;
  /**
   * The box opens with whatever the launcher seeded — "About PU_DASH_…: " — and
   * it is an ordinary editable value from then on. The sheet is mounted fresh
   * on every open, so this initial value is read once per open and the
   * dispatcher's own edits are never overwritten under them.
   *
   * Deliberately NOT focused on mount: PortalModal (@restart/ui) moves focus
   * into the dialog itself when it opens, and a child grabbing it first only
   * loses a race. It also means the iOS keyboard does not come up over the
   * board before anyone has asked to type.
   */
  const [draft, setDraft] = useState(assistant.seed || '');
  const threadRef = useRef(null);
  /**
   * WHERE THE CARET GOES IN A SEEDED BOX, which is not where the browser puts
   * it.
   *
   * The seed is a PREFIX to be continued — "About PU_DASH_…: " — so the only
   * sensible caret position in a box nobody has typed in yet is after it. What
   * actually happens is that a tap places the caret wherever the finger landed,
   * which for a one-line box holding text is somewhere in the MIDDLE of the
   * reference. The harness caught it exactly: the composer held "About the
   * board for Thursday 8 October: ", a question was typed, and it came out as
   * "About the boar" + the question + "d for Thursday 8 October:".
   *
   * So on the first interaction the caret is moved to the end. The guard is
   * that the box still holds EXACTLY what was seeded: the instant one character
   * is typed the guard fails and the caret is never touched again, so a
   * dispatcher who wants to go back and edit the reference can, and is not
   * fought by this.
   *
   * It runs on click as well as focus, and both are needed: a pointer places
   * its own caret on mousedown, which is AFTER `focus` has fired, so a focus
   * handler on its own gets overwritten by the very tap that triggered it.
   */
  const seedRef = useRef(assistant.seed || '');
  const boxRef = useRef(null);

  /**
   * The thread, with a key per block that is unique even when the AGENT is not.
   *
   * Block ids come off the wire. `useChatAgent` merges a `text.delta` into the
   * last block only while that block is still STREAMING, so a later turn whose
   * text arrives under an id already used — a retried question, a model that
   * reuses a label — appends a second block carrying the first one's id, and
   * React then renders one of the two and drops the other. Numbering repeats
   * keeps every key distinct without putting the array index in it, and the
   * list is append-only so a block's number never changes under it.
   *
   * The dashboard's ChatThread keys on the bare id and has the same gap. Fixing
   * it there means changing how the reducer mints ids, and the ingest grid
   * looks blocks up BY id (`proposal`, `commit_result`, a question's answer),
   * so that is a change with a blast radius and this is not.
   */
  const keyed = useMemo(() => {
    const seen = new Map();
    return blocks.map((block) => {
      const n = (seen.get(block.id) || 0) + 1;
      seen.set(block.id, n);
      return { block, key: n === 1 ? String(block.id) : `${block.id}#${n}` };
    });
  }, [blocks]);

  // Follow the answer as it streams. `scrollTop` rather than `scrollTo`: the
  // latter does not exist on an element in jsdom, and this needs no easing.
  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [blocks, pending]);

  // Grow with the question, up to five lines, then scroll inside the box. A
  // dispatcher pasting a whole itinerary in should be able to read it back.
  const grow = useCallback(() => {
    const el = boxRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, []);

  const submit = () => {
    const text = draft.trim();
    if (!text || pending) return;
    setDraft('');
    send(text);
    // The box is empty again, so it must shrink back to one line. Set
    // directly rather than through `grow()`: the textarea still holds the old
    // value at this point, so its scrollHeight is the height we are undoing.
    if (boxRef.current) boxRef.current.style.height = 'auto';
  };

  /**
   * Put the last question back on the wire. The one tap that gets a dropped
   * stream unstuck: the fragment stays in the thread (marked), and the same
   * words are asked again. The agent's own memory of the session is
   * server-side, so it is not being asked cold.
   */
  const askAgain = () => {
    const last = [...blocks].reverse().find((b) => b.kind === 'text' && b.role === 'user');
    if (last?.content) send(last.content);
  };

  const fill = (text) => {
    setDraft(text);
    boxRef.current?.focus();
  };

  const caretAfterSeed = useCallback(() => {
    const el = boxRef.current;
    // Untouched, still exactly the seed, and there is a seed at all.
    if (!el || !seedRef.current || el.value !== seedRef.current) return;
    const end = el.value.length;
    if (el.selectionStart !== end || el.selectionEnd !== end) el.setSelectionRange(end, end);
  }, []);

  return (
    <BottomSheet
      title={SHEET_TITLE}
      subtitle={SHEET_SUBTITLE}
      onClose={assistant.close}
      closeLabel="Close"
    >
      <div className="pp-ai">
        {/* `tabIndex={0}` because this is a SCROLLABLE REGION, and a region a
            mouse can scroll but a keyboard cannot reach is axe's
            `scrollable-region-focusable` — a dispatcher on a phone keyboard, or
            anyone not using a pointer, could not read back up a long answer.
            It is not a widget: it takes focus so it can be scrolled, and the
            label is what a screen reader announces when it lands.

            NOT `role="log"`, which carries an implicit `aria-live="polite"`:
            that would have a screen reader announce a streaming answer delta by
            delta. The state line below the thread is the live region, and it
            says one sentence per turn. */}
        <div
          className="pp-ai__thread"
          ref={threadRef}
          aria-label="Conversation"
          // A scrollable region MUST be focusable (WCAG 2.1.1, and axe's
          // `scrollable-region-focusable`), and `no-noninteractive-tabindex`
          // cannot see that this div scrolls. The two rules disagree and the
          // keyboard user wins: without a tab stop there is no way to read back
          // up a long answer without a pointer.
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
          tabIndex={0}
        >
          {blocks.length ? keyed.map(({ block, key }) => (
            <AssistantBlock
              key={key}
              block={block}
              onAnswer={answer}
              onDecideApproval={decideApproval}
            />
          )) : <Openers onPick={fill} />}
        </div>

        <TurnState chat={chat} onAskAgain={askAgain} />

        <form
          className="pp-ai__composer"
          onSubmit={(event) => { event.preventDefault(); submit(); }}
        >
          <label className="pp-ai__box-label" htmlFor="pp-ai-box">{PLACEHOLDER}</label>
          <textarea
            id="pp-ai-box"
            ref={boxRef}
            className="pp-input pp-input--area pp-ai__box"
            rows={1}
            value={draft}
            placeholder={PLACEHOLDER}
            onChange={(event) => { setDraft(event.target.value); grow(); }}
            // Both, on purpose — see `seedRef` above: `focus` covers arriving
            // by keyboard, `click` runs after a tap has placed its own caret.
            onFocus={caretAfterSeed}
            onClick={caretAfterSeed}
            // Enter sends, Shift+Enter makes a line — the same as the
            // dashboard's composer and the passenger message field, so the
            // key does one thing across the whole product.
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                submit();
              }
            }}
          />
          {/* STOP, not a disabled Send. Every automatic recovery depends on
              noticing the hang; this one does not, which is why it replaces
              the send control rather than sitting beside it. */}
          {pending ? (
            <button
              type="button"
              className="pp-btn pp-btn--outline pp-ai__act"
              onClick={stop}
            >
              <StopIcon size={18} />
              {STOP}
            </button>
          ) : (
            <button
              type="submit"
              className="pp-btn pp-btn--dark pp-ai__act"
              disabled={!draft.trim()}
            >
              <SendIcon size={18} />
              {SEND}
            </button>
          )}
        </form>

        <div className="pp-ai__foot">
          <p className="pp-sheet__note">{PRIVACY_NOTE}</p>
          {blocks.length ? (
            <button type="button" className="pp-btn pp-btn--sm pp-ai__quiet" onClick={reset}>
              {NEW_CHAT}
            </button>
          ) : null}
        </div>
      </div>
    </BottomSheet>
  );
}
